import React from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Grid, Typography, Box, CircularProgress, Alert } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useBlogPostBySlug } from '../hooks/useBlogPost';
import AdminLink from '../components/admin/AdminLink';
import homestyles from '../css/homestyles.module.css';

// DEBUG: Verify import
console.log('[BlogEntry] AdminLink imported:', AdminLink, typeof AdminLink);

const BlogEntry = () => {
    const { slug } = useParams();
    const location = useLocation();
    
    // Try to get slug from URL params first, then from location state (for backward compatibility)
    const postSlug = slug || (location.state?.slug || location.state?._id || location.state?.id);
    
    // Debug logging
    console.log('BlogEntry - slug from params:', slug);
    console.log('BlogEntry - location.state:', location.state);
    console.log('BlogEntry - final postSlug:', postSlug);
    
    // Use the hook to fetch blog post from markdown files
    const { blog, isLoading, error } = useBlogPostBySlug(postSlug || null);
    
    // Debug logging for hook results
    console.log('BlogEntry - isLoading:', isLoading);
    console.log('BlogEntry - error:', error);
    console.log('BlogEntry - blog:', blog);

    // Loading state
    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    // Error state
    if (error) {
        return (
            <Box sx={{ margin: 2 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    // No blog found
    if (!blog) {
        return (
            <Box sx={{ margin: 2 }}>
                <Alert severity="warning">Blog post not found</Alert>
            </Box>
        );
    }

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return 'Date not available';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
            });
        } catch {
            return dateString;
        }
    };

    const editId = blog?.id ?? blog?._id;
    
    // DEBUG: Log editId and whether AdminLink will render
    console.log('[BlogEntry] editId:', editId, 'blog.id:', blog?.id, 'blog._id:', blog?._id, 'will render AdminLink:', !!editId);

    return (
        <Box sx={{ flexGrow: 1, margin: 2, height: '100%' }}>
            <Grid container spacing={1} style={{ marginTop: 2 }} className={homestyles['primary-background']}>
                <Grid item xs={0} sm={0} md={2} lg={2} xl={2} />
                <Grid item container direction={'row'} xs={12} sm={12} md={7} lg={7} xl={7} spacing={1}>
                    <Grid item sm={1} md={1} lg={1} />
                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Box style={{ backgroundColor: 'rgba(255,255,255,0.8)' }} sx={{ p: 2, border: '1px hidden' }}>
                            <Grid item container direction={'column'} spacing={2}>
                                <Grid item>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant='h4' component="h1">
                                            {blog.title}
                                        </Typography>
                                        {/* Show Edit button - use _id or id (handle both API and markdown sources) */}
                                        {editId ? (
                                            <>
                                                {console.log('[BlogEntry] About to render AdminLink with editId:', editId)}
                                                <AdminLink to={`/admin/blog/edit/${editId}`} variant="outlined" color="primary">
                                                    <EditIcon sx={{ mr: 0.5 }} />
                                                    Edit
                                                </AdminLink>
                                            </>
                                        ) : (
                                            console.log('[BlogEntry] NOT rendering AdminLink - editId is falsy')
                                        )}
                                    </Box>
                                </Grid>
                                <Grid item>
                                    <Typography variant='body2' color="text.secondary">
                                        {formatDate(blog.publishedDate)}
                                        {blog.readingTime && ` • ${blog.readingTime} min read`}
                                    </Typography>
                                </Grid>
                                {blog.excerpt && (
                                    <Grid item>
                                        <Typography variant='body1' sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                            {blog.excerpt}
                                        </Typography>
                                    </Grid>
                                )}
                                <Grid item>
                                    <Box
                                        sx={{
                                            '& h1': { fontSize: '2em', fontWeight: 'bold', mb: 1, mt: 2 },
                                            '& h2': { fontSize: '1.5em', fontWeight: 'bold', mb: 1, mt: 2 },
                                            '& h3': { fontSize: '1.25em', fontWeight: 'bold', mb: 1, mt: 2 },
                                            '& p': { mb: 1.5, lineHeight: 1.6 },
                                            '& ul, & ol': { mb: 1.5, pl: 3 },
                                            '& li': { mb: 0.5 },
                                            '& code': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                                padding: '2px 6px',
                                                borderRadius: '3px',
                                                fontFamily: 'monospace',
                                                fontSize: '0.9em',
                                            },
                                            '& pre': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                                padding: '12px',
                                                borderRadius: '4px',
                                                overflow: 'auto',
                                                mb: 1.5,
                                            },
                                            '& pre code': {
                                                backgroundColor: 'transparent',
                                                padding: 0,
                                            },
                                            '& blockquote': {
                                                borderLeft: '4px solid',
                                                borderColor: 'primary.main',
                                                pl: 2,
                                                ml: 0,
                                                fontStyle: 'italic',
                                                mb: 1.5,
                                            },
                                        }}
                                    >
                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                            {blog.content}
                                        </ReactMarkdown>
                                    </Box>
                                </Grid>
                                {blog.categories && blog.categories.length > 0 && (
                                    <Grid item>
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                                            {blog.categories.map((category, index) => (
                                                <Typography 
                                                    key={index}
                                                    variant='caption' 
                                                    sx={{ 
                                                        backgroundColor: 'primary.light', 
                                                        color: 'primary.contrastText',
                                                        px: 1,
                                                        py: 0.5,
                                                        borderRadius: 1
                                                    }}
                                                >
                                                    {category}
                                                </Typography>
                                            ))}
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    </Grid>
                </Grid>
                <Grid item xs={0} sm={0} md={2} lg={0} xl={0} />
            </Grid>
        </Box>
    );
}

export default BlogEntry;
